/**
 * Diagramas do projeto — a gestão deles, e agora também o traço.
 *
 * **O desenho aparece na aba Desenho da ficha ao lado.** O que a lista mostra é a
 * coluna `svg`, que é **de onde o site do cliente serve** — o desenho publicado,
 * para 3.815 dos 4.829 diagramas. Quando ele for anterior às últimas edições, a
 * ficha diz isso em cima do traço.
 *
 * **Redesenhar já é daqui**: o `Desenhar()` foi portado e a aba Desenho tem os
 * três botões — conferir sem gravar, ver a prévia e gravar. O `GET {id}/svg` que
 * existe lista *anexos*, não o desenho — são coisas diferentes, e a ficha separa
 * as duas.
 */

import { useState } from "react"
import { Link, useParams } from "react-router-dom"
import {
  usarCriarDiagrama, usarDiagramas, usarDuplicarDiagrama, usarExcluirDiagrama, usarGerarDiagrama,
  usarMandarParaRevisao, usarPublicarDiagrama, type DiagramaResponse,
} from "@/api/diagramas"
import { Secao } from "@/kit/Secao"
import { AlcaDeLargura, usarLarguraArrastavel } from "@/kit/LarguraArrastavel"
import { usarComponentes } from "@/api/catalogos"
import { POR_PAGINA } from "@/api/query"
import { ErroDaApi, ErroDeTrava } from "@/api/erros"
import { LinhaDeAviso, Tabela, type Coluna } from "@/kit/Tabela"
import { BarraDeEstado } from "@/kit/BarraDeEstado"
import { DetalhesDoDiagrama } from "./DetalhesDoDiagrama"
import { SeletorDeCorDeFuncao } from "./SeletorDeCorDeFuncao"

const MODOS: Record<number, string> = { 0: "Normal", 1: "Interno", 2: "Agrupado" }

const dia = (d: string | null) => {
  if (!d || d.slice(0, 4) === "1900" || d.slice(0, 4) === "0001") return ""
  const [a, m, x] = d.slice(0, 10).split("-")
  return `${x}/${m}/${a}`
}

export function Diagramas() {
  const { projetoId } = useParams()
  const id = Number(projetoId)

  const [pagina, setPagina] = useState(1)
  const [aberto, setAberto] = useState<DiagramaResponse | null>(null)
  const [criando, setCriando] = useState(false)

  const { data, isPending, error } = usarDiagramas(id, pagina)
  const itens = data?.itens ?? []
  const total = data?.total ?? 0
  const paginas = Math.max(1, Math.ceil(total / POR_PAGINA))

  const colunas: Coluna<DiagramaResponse>[] = [
    { chave: "id", rotulo: "Diagrama", largura: 90, mono: true, alinhamento: "direita", render: (d) => d.id },
    {
      chave: "componente", rotulo: "Componente", minimo: 230,
      render: (d) => {
        const codigo = (d.componenteCodigo ?? "").trim()
        const desc = (d.componenteDescricao ?? "").trim()
        if (!codigo && !desc) return <span style={{ color: "var(--erro)" }}>sem componente</span>
        const repetida = desc.toLowerCase() === codigo.toLowerCase()
        return (
          <>
            {codigo && <span style={{ fontFamily: "var(--fm)", fontWeight: 600 }}>{codigo}</span>}
            {codigo && desc && !repetida && " "}
            {!repetida && desc}
          </>
        )
      },
    },
    { chave: "modo", rotulo: "Modo", largura: 100, render: (d) => MODOS[d.modo] ?? String(d.modo) },
    { chave: "versao", rotulo: "Versão", largura: 76, mono: true, alinhamento: "direita", render: (d) => d.versao },
    {
      chave: "estado", rotulo: "Estado", largura: 170,
      render: (d) => (
        <span style={{ display: "inline-flex", gap: 5 }}>
          {d.publicado && <Selo tom="ok">publicado</Selo>}
          {d.revisao && <Selo tom="acao">em revisão</Selo>}
          {!d.publicado && !d.revisao && <span style={{ color: "var(--txt3)" }}>—</span>}
        </span>
      ),
    },
    { chave: "obs", rotulo: "Observação", minimo: 140, render: (d) => d.obs ?? "" },
  ]

  return (
    <div style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
      <div
        className="barra"
        style={{
          flex: "0 0 auto", display: "flex", alignItems: "center", gap: 10,
          padding: "9px 18px", background: "var(--acao-soft)", color: "var(--acao-on-soft)",
          borderBottom: "1px solid var(--acao)", fontSize: 11.5, lineHeight: 1.5,
        }}
      >
        <b>O traço da lista é o desenho publicado.</b>
        <span style={{ opacity: 0.9 }}>
          É o SVG gravado — o mesmo que o site do cliente serve — e a ficha avisa quando ele é
          anterior às últimas edições. Para refazer, use Redesenhar na aba Desenho.
        </span>
        <button
          onClick={() => { setCriando((c) => !c); setAberto(null) }}
          style={{
            marginLeft: "auto", padding: "4px 10px", fontSize: 11.5, fontWeight: 600,
            border: "1px solid var(--acao)", borderRadius: 5, cursor: "pointer",
            background: "var(--surface)", color: "var(--txt)",
          }}>
          {criando ? "Cancelar" : "Novo diagrama"}
        </button>
      </div>

      {criando && (
        <NovoDiagrama projetoId={id} aoCriar={(d) => { setCriando(false); setAberto(d) }} />
      )}

      <div style={{ flex: 1, minHeight: 0, display: "flex" }}>
        <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column" }}>
          <Tabela<DiagramaResponse>
            colunas={colunas}
            itens={itens}
            chaveDe={(d) => d.id}
            selecionada={aberto?.id ?? null}
            aoSelecionar={(d) => setAberto(d)}
            vazio={
              isPending ? <LinhaDeAviso>Carregando os diagramas…</LinhaDeAviso>
              : error ? (
                <LinhaDeAviso>
                  <b style={{ color: "var(--erro)" }}>
                    {error instanceof ErroDaApi ? error.message : "Não foi possível falar com o servidor."}
                  </b>
                </LinhaDeAviso>
              ) : <LinhaDeAviso>Este projeto ainda não tem diagrama.</LinhaDeAviso>
            }
          />
        </div>

        {aberto && (
          <PainelDoDiagrama
            // A chave zera os rascunhos (observação, ficha, confirmação) ao trocar de diagrama.
            key={aberto.id}
            projetoId={id}
            diagrama={itens.find((d) => d.id === aberto.id) ?? aberto}
            aoFechar={() => setAberto(null)}
          />
        )}
      </div>

      <BarraDeEstado
        direita={
          paginas > 1 ? (
            <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <Passo ligado={pagina > 1} rotulo="Página anterior" aoIr={() => setPagina(pagina - 1)} texto="‹" />
              <span className="num" style={{ minWidth: 62, textAlign: "center" }}>{pagina} de {paginas}</span>
              <Passo ligado={pagina < paginas} rotulo="Próxima página" aoIr={() => setPagina(pagina + 1)} texto="›" />
            </span>
          ) : null
        }
      >
        {total > 0 && (
          <span>
            <b className="num" style={{ color: "var(--txt)" }}>{total}</b>{" "}
            {total === 1 ? "diagrama" : "diagramas"}
            {" · "}
            <b className="num" style={{ color: "var(--txt)" }}>{itens.filter((d) => d.publicado).length}</b> no ar
            {itens.some((d) => d.revisao) && (
              <> · <b className="num" style={{ color: "var(--txt)" }}>
                {itens.filter((d) => d.revisao).length}</b> em revisão</>
            )}
          </span>
        )}
      </BarraDeEstado>
    </div>
  )
}

/**
 * O diagrama novo — o `IncluirFinalizar` do sistema antigo.
 *
 * **Nasce vazio.** Criar não desenha nada: monta a linha com o componente
 * principal e os metadados, e quem põe componentes e ligações lá dentro é o
 * **Gerar**, que lê o pinout. Por isso o botão leva direto para a ficha, onde o
 * Gerar está.
 *
 * **Versão 2 é a de hoje** e a tela não oferece a 1: ela existe no acervo antigo
 * e os dois caminhos do motor são diferentes — criar uma nova na 1 seria criar
 * dívida.
 */
function NovoDiagrama({ projetoId, aoCriar }: {
  projetoId: number
  aoCriar: (d: DiagramaResponse) => void
}) {
  const componentes = usarComponentes(projetoId)
  const criar = usarCriarDiagrama(projetoId)

  const [componenteId, setComponenteId] = useState(0)
  const [posicao, setPosicao] = useState("center")
  const [modo, setModo] = useState(0)
  const [cor, setCor] = useState("#E6E7E8")
  const [recado, setRecado] = useState<string | null>(null)

  const entrada: React.CSSProperties = {
    padding: "4px 7px", fontSize: 11.5, border: "1px solid var(--line)",
    borderRadius: 5, background: "var(--surface)", color: "var(--txt)",
  }
  const rotulo: React.CSSProperties = {
    fontSize: 10, color: "var(--acao)", textTransform: "uppercase", letterSpacing: 0.4,
  }

  return (
    <div style={{ flex: "0 0 auto", padding: "10px 18px", background: "var(--surface2)",
                  borderBottom: "1px solid var(--line2)" }}>
      <div style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "flex-end" }}>
        <label style={{ display: "flex", flexDirection: "column", gap: 3 }}>
          <span style={rotulo}>Componente principal</span>
          <select value={componenteId} onChange={(e) => setComponenteId(Number(e.target.value))}
                  style={{ ...entrada, width: 320 }}>
            <option value={0}>
              {componentes.carregando ? "carregando…" : "escolha o componente"}
            </option>
            {componentes.itens.map((c) => (
              <option key={c.id} value={c.id}>{c.codigo} {c.rotulo}</option>
            ))}
          </select>
        </label>

        <label style={{ display: "flex", flexDirection: "column", gap: 3 }}>
          <span style={rotulo}>Posição</span>
          <select value={posicao} onChange={(e) => setPosicao(e.target.value)}
                  style={{ ...entrada, width: 110 }}>
            <option value="center">Centro</option>
            <option value="left">Esquerda</option>
            <option value="right">Direita</option>
          </select>
        </label>

        <label style={{ display: "flex", flexDirection: "column", gap: 3 }}>
          <span style={rotulo}>Modo</span>
          <select value={modo} onChange={(e) => setModo(Number(e.target.value))}
                  style={{ ...entrada, width: 120 }}>
            {Object.entries(MODOS).map(([v, nome]) => (
              <option key={v} value={v}>{nome}</option>
            ))}
          </select>
        </label>

        {/* A cor do principal é uma cor de função, como no legado — não RGB livre. */}
        <div role="group" aria-label="Cor do principal"
             style={{ display: "flex", flexDirection: "column", gap: 3, flexBasis: "100%" }}>
          <span style={rotulo}>Cor do principal</span>
          <SeletorDeCorDeFuncao valor={cor} aoMudar={setCor} semCor={false} rotulo="Cor do principal" />
        </div>

        <button
          disabled={componenteId === 0 || criar.isPending}
          onClick={() => {
            setRecado(null)
            criar.mutate(
              { componenteId, modo, posicao, versao: 2, sistemaId: 0, cor, modelos: [] },
              {
                onSuccess: (d) => aoCriar(d),
                onError: (e) => setRecado(
                  e instanceof ErroDeTrava || e instanceof ErroDaApi
                    ? e.message : "Não foi possível criar o diagrama."),
              })
          }}
          style={{ ...entrada, cursor: componenteId === 0 ? "default" : "pointer",
                   fontWeight: 600, opacity: componenteId === 0 ? 0.5 : 1 }}>
          {criar.isPending ? "Criando…" : "Criar diagrama"}
        </button>

        <span style={{ fontSize: 10.5, color: "var(--txt3)", maxWidth: "42ch", lineHeight: 1.5 }}>
          Nasce vazio, na versão 2. Quem põe componentes e ligações é o <b>Gerar</b>, na ficha.
        </span>
      </div>

      {recado && (
        <p style={{ margin: "8px 0 0", fontSize: 11.5, color: "var(--erro)" }}>{recado}</p>
      )}
    </div>
  )
}

/**
 * A barra lateral do diagrama.
 *
 * **Montar vem primeiro**, em destaque: é para lá que se vai na maior parte das
 * vezes. Embaixo, cada assunto num título com setinha que abre e fecha — fechados,
 * cabem todos na tela de uma vez. **Excluir é o último**, fechado, para não ser
 * clicado de passagem.
 *
 * A largura se arrasta pela borda esquerda e fica lembrada no navegador.
 */
function PainelDoDiagrama({ projetoId, diagrama, aoFechar }: {
  projetoId: number
  diagrama: DiagramaResponse
  aoFechar: () => void
}) {
  const publicar = usarPublicarDiagrama(projetoId)
  const revisao = usarMandarParaRevisao(projetoId)
  const gerar = usarGerarDiagrama(projetoId)
  const duplicar = usarDuplicarDiagrama(projetoId)
  const excluir = usarExcluirDiagrama(projetoId)
  const largura = usarLarguraArrastavel({ chave: "painel-do-diagrama", padrao: 380 })

  const [confirmando, setConfirmando] = useState<"gerar" | "excluir" | null>(null)

  const erro = publicar.error ?? revisao.error ?? gerar.error ?? duplicar.error ?? excluir.error
  const discrepancias = publicar.data?.discrepancias ?? []

  return (
    <aside
      className="gaveta"
      style={{
        flex: `0 0 ${largura.largura}px`, width: largura.largura, borderLeft: "1px solid var(--line2)",
        background: "var(--surface)", display: "flex", flexDirection: "column", minHeight: 0,
        position: "relative",
      }}
    >
      <AlcaDeLargura {...largura} />
      <div style={{
        flex: "0 0 auto", display: "flex", alignItems: "center", gap: 8,
        padding: "0 14px", height: 44, borderBottom: "1px solid var(--line)",
      }}>
        <span style={{ flex: 1, minWidth: 0, fontSize: 13.5, fontWeight: 600 }}>
          Diagrama <span className="num">{diagrama.id}</span>
        </span>
        <button onClick={aoFechar} aria-label="Fechar o painel"
                style={{ display: "flex", color: "var(--txt3)", padding: 4 }}>
          <svg width="14" height="14" viewBox="0 0 24 24" aria-hidden="true"
               style={{ fill: "none", stroke: "currentColor", strokeWidth: 2.2, strokeLinecap: "round" }}>
            <path d="M6 6l12 12M18 6 6 18" />
          </svg>
        </button>
      </div>

      <div style={{ flex: 1, minHeight: 0, overflowY: "auto", padding: 14,
                    display: "flex", flexDirection: "column", gap: 0 }}>
        <div style={{ display: "flex", flexDirection: "column", gap: 3, marginBottom: 10 }}>
          <span className="mono" style={{ fontSize: 12.5, fontWeight: 600 }}>
            {diagrama.componenteCodigo || diagrama.componenteDescricao || "—"}
            {diagrama.componenteCodigo && diagrama.componenteDescricao &&
              diagrama.componenteDescricao.trim().toLowerCase() !== diagrama.componenteCodigo.trim().toLowerCase() && (
                <span style={{ fontFamily: "var(--ff)", fontWeight: 400 }}> {diagrama.componenteDescricao}</span>
              )}
          </span>
          <span style={{ fontSize: 11, color: "var(--txt3)" }}>
            modo {MODOS[diagrama.modo] ?? diagrama.modo} · versão {diagrama.versao}
            {diagrama.publicadoData && dia(diagrama.publicadoData) &&
              ` · publicado em ${dia(diagrama.publicadoData)}`}
          </span>
        </div>

        <Link
          to={`/projetos/${projetoId}/diagramas/${diagrama.id}/montar`}
          style={{
            display: "flex", alignItems: "center", gap: 10, padding: "11px 13px", marginBottom: 12,
            borderRadius: 9, textDecoration: "none",
            background: "var(--brand)", color: "#FFFFFF",
          }}>
          <span style={{ display: "flex", flexDirection: "column", gap: 2, flex: 1, minWidth: 0 }}>
            <b style={{ fontSize: 13.5 }}>Montar o diagrama</b>
            <span style={{ fontSize: 11, opacity: 0.88, lineHeight: 1.4 }}>
              As duas colunas com o desenho de cada componente: arrastar, trocar de lado, agrupar.
            </span>
          </span>
          <span aria-hidden="true" style={{ fontSize: 18, fontWeight: 600 }}>→</span>
        </Link>

        <Secao titulo="Publicação e revisão" chave="diagrama-publicacao" aberta
               resumo={diagrama.publicado ? "publicado" : diagrama.revisao ? "em revisão" : "não publicado"}>
          <Acao
            principal={!diagrama.publicado}
            rotulo={diagrama.publicado ? "Despublicar" : "Publicar"}
            ajuda={diagrama.publicado
              ? "Tira o diagrama da vista do cliente."
              : "Põe o diagrama à vista do cliente. A API confere pendências antes."}
            ocupado={publicar.isPending}
            aoAgir={() => publicar.mutate({ id: diagrama.id, publicar: !diagrama.publicado })}
          />

          <Acao
            rotulo={diagrama.revisao ? "Tirar da revisão" : "Mandar para revisão"}
            ajuda="A revisão é a fila de quem confere antes de publicar."
            ocupado={revisao.isPending}
            aoAgir={() => revisao.mutate({ id: diagrama.id, enviar: !diagrama.revisao })}
          />

          {/* RN-08: publicar pode voltar com pendências em vez de publicar. Elas
              são o motivo, então aparecem inteiras — resumir esconderia o que a
              pessoa precisa corrigir. */}
          {discrepancias.length > 0 && (
            <div style={{
              padding: "10px 12px", borderRadius: 8, fontSize: 11.5, lineHeight: 1.5,
              background: "var(--acao-soft)", color: "var(--acao-on-soft)", border: "1px solid var(--acao)",
            }}>
              <b style={{ display: "block", marginBottom: 4 }}>
                {discrepancias.length} pendência{discrepancias.length > 1 ? "s" : ""} impedindo a publicação
              </b>
              <ul style={{ margin: 0, paddingLeft: 16 }}>
                {discrepancias.slice(0, 12).map((d, i) => (
                  <li key={i} className="mono" style={{ fontSize: 11 }}>
                    {Object.entries(d).map(([k, v]) => `${k}: ${String(v)}`).join(" · ")}
                  </li>
                ))}
              </ul>
              {discrepancias.length > 12 && (
                <span style={{ fontSize: 11 }}>…e mais {discrepancias.length - 12}.</span>
              )}
            </div>
          )}
        </Secao>

        <DetalhesDoDiagrama projetoId={projetoId} diagrama={diagrama} />

        <Secao titulo="Gerar e duplicar" chave="diagrama-gerar">
          {confirmando === "gerar" ? (
            <Confirmar
              texto="Gerar reescreve os componentes e as ligações deste diagrama a partir dos pinouts."
              rotulo={gerar.isPending ? "Gerando…" : "Confirmar geração"}
              aoConfirmar={() => { gerar.mutate(diagrama.id); setConfirmando(null) }}
              aoCancelar={() => setConfirmando(null)}
            />
          ) : (
            <Acao
              rotulo="Gerar o conteúdo"
              ajuda="Monta componentes e ligações a partir dos pinouts. INSERE: rodar de novo duplica o que já está lá — para refazer do zero, use Restaurar."
              aoAgir={() => setConfirmando("gerar")}
            />
          )}

          <Acao
            rotulo="Duplicar"
            ajuda="Cria uma cópia. Só insere — não apaga nada."
            ocupado={duplicar.isPending}
            aoAgir={() => duplicar.mutate(diagrama.id)}
          />
        </Secao>

        {/* Por último e fechado: excluir não pode estar no caminho de quem só passa. */}
        <Secao titulo="Excluir o diagrama" chave="diagrama-excluir" perigo>
          {confirmando === "excluir" ? (
            <Confirmar
              texto="Excluir o diagrama. Dá para restaurar depois."
              rotulo={excluir.isPending ? "Excluindo…" : "Confirmar exclusão"}
              perigo
              aoConfirmar={() => { excluir.mutate({ id: diagrama.id, restaurar: false }); setConfirmando(null) }}
              aoCancelar={() => setConfirmando(null)}
            />
          ) : (
            <Acao
              rotulo="Excluir"
              ajuda="Exclusão lógica (RN-15): dá para restaurar."
              perigo
              aoAgir={() => setConfirmando("excluir")}
            />
          )}
        </Secao>

        {erro != null && (
          <div style={{
            marginTop: 12, padding: "10px 12px", borderRadius: 8, fontSize: 11.5, lineHeight: 1.5,
            background: erro instanceof ErroDeTrava ? "var(--acao-soft)" : "var(--erro-soft)",
            color: erro instanceof ErroDeTrava ? "var(--acao-on-soft)" : "var(--erro-on-soft)",
          }}>
            {erro instanceof Error ? erro.message : "Não foi possível concluir."}
          </div>
        )}
      </div>
    </aside>
  )
}

function Acao({ rotulo, ajuda, aoAgir, principal, perigo, ocupado }: {
  rotulo: string; ajuda: string; aoAgir: () => void
  principal?: boolean; perigo?: boolean; ocupado?: boolean
}) {
  return (
    <button
      onClick={aoAgir}
      disabled={ocupado}
      style={{
        display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 2,
        width: "100%", padding: "9px 11px", borderRadius: 8, textAlign: "left",
        background: principal ? "var(--brand)" : "var(--surface2)",
        color: principal ? "#FFFFFF" : perigo ? "var(--erro)" : "var(--txt)",
        border: principal ? undefined : `1px solid ${perigo ? "var(--erro)" : "var(--line2)"}`,
        opacity: ocupado ? 0.6 : 1,
      }}
    >
      <span style={{ fontSize: 12.5, fontWeight: 600 }}>{ocupado ? "Aguarde…" : rotulo}</span>
      <span style={{ fontSize: 11, opacity: principal ? 0.85 : 0.7, color: perigo ? "var(--txt2)" : undefined }}>
        {ajuda}
      </span>
    </button>
  )
}

function Confirmar({ texto, rotulo, aoConfirmar, aoCancelar, perigo }: {
  texto: string; rotulo: string; aoConfirmar: () => void; aoCancelar: () => void; perigo?: boolean
}) {
  return (
    <div style={{
      padding: "10px 11px", borderRadius: 8, display: "flex", flexDirection: "column", gap: 8,
      background: "var(--acao-soft)", color: "var(--acao-on-soft)", border: "1px solid var(--acao)",
    }}>
      <span style={{ fontSize: 11.5, lineHeight: 1.5 }}>{texto}</span>
      <span style={{ display: "flex", gap: 6 }}>
        <button
          onClick={aoConfirmar}
          style={{
            height: 28, padding: "0 11px", borderRadius: 7, fontSize: 12, fontWeight: 600,
            background: perigo ? "var(--erro)" : "var(--acao)",
            color: perigo ? "#FFFFFF" : "var(--acao-ink)",
          }}
        >
          {rotulo}
        </button>
        <button onClick={aoCancelar} style={{ height: 28, padding: "0 10px", fontSize: 12, color: "var(--txt2)" }}>
          Cancelar
        </button>
      </span>
    </div>
  )
}

function Selo({ tom, children }: { tom: "ok" | "acao"; children: React.ReactNode }) {
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", height: 19, padding: "0 8px",
      borderRadius: 999, fontSize: 10.5, fontWeight: 600,
      background: tom === "ok" ? "var(--ok-soft)" : "var(--acao-soft)",
      color: tom === "ok" ? "var(--ok-on-soft)" : "var(--acao-on-soft)",
    }}>
      {children}
    </span>
  )
}

function Passo({ ligado, rotulo, aoIr, texto }: {
  ligado: boolean; rotulo: string; aoIr: () => void; texto: string
}) {
  return (
    <button
      disabled={!ligado} onClick={aoIr} aria-label={rotulo}
      style={{
        display: "flex", alignItems: "center", justifyContent: "center",
        width: 22, height: 22, borderRadius: 5, border: "1px solid var(--line2)",
        background: "var(--surface)", color: "var(--txt2)", fontSize: 13,
        opacity: ligado ? 1 : 0.4, cursor: ligado ? "pointer" : "default",
      }}
    >
      {texto}
    </button>
  )
}
